import React, {FC, useState, useMemo} from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Alert,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import {ZODIAC_SIGNS, getZodiacSign, getZodiacTitle} from '../../../utils/zodiac';

export const ZodiacIcon: FC<{
  signKey: string;
  size?: number;
  color?: string;
}> = ({signKey, size = 20, color = '#ffffff'}) => {
  const sign = getZodiacSign(signKey);
  if (!sign || !sign.svgPath) return null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill={color}
      style={{display: 'block', flexShrink: 0}}
    >
      <path d={sign.svgPath}/>
    </svg>
  );
};

export const ZodiacBadge: FC<{
  signKey: string;
  size?: number;
  showTitle?: boolean;
  fontSize?: number | string;
  shape?: 'squircle' | 'circle';
  iconSize?: number;
}> = ({signKey, size = 30, showTitle = true, fontSize = '0.85rem', shape = 'squircle', iconSize}) => {
  const sign = getZodiacSign(signKey);
  if (!sign) return <span>{signKey}</span>;

  const borderRadius = shape === 'squircle' ? Math.max(5, Math.round(size * 0.28)) : '50%';
  const effectiveIconSize = iconSize ?? (size <= 30 ? 20 : Math.round(size * 0.65));

  return (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.8, verticalAlign: 'middle'}}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: `${borderRadius}px`,
          background: sign.bgGradient,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 5px rgba(0,0,0,0.16)',
          userSelect: 'none',
          flexShrink: 0
        }}
        title={`${sign.title} • ${sign.elementTitle} • ${sign.dates}`}
      >
        <ZodiacIcon signKey={sign.key} size={effectiveIconSize} color="#ffffff"/>
      </Box>
      {showTitle && (
        <Typography component="span" sx={{fontWeight: 600, fontSize, color: '#263238'}}>
          {sign.title}
        </Typography>
      )}
    </Box>
  );
};

interface ZodiacMatrixTableProps {
  zodiacPairs: Record<string, string>;
  onSavePair: (userSign: string, partnerSign: string, text: string, applyBothWays: boolean) => void;
  onDeletePair: (key: string, reverseKey?: string) => void;
}

export const ZodiacMatrixTable: FC<ZodiacMatrixTableProps> = ({
                                                                zodiacPairs,
                                                                onSavePair,
                                                                onDeletePair
                                                              }) => {
  const [viewMode, setViewMode] = useState<'matrix' | 'list'>('matrix');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Состояние модального окна редактирования
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogUserSign, setDialogUserSign] = useState<string>('pisces');
  const [dialogPartnerSign, setDialogPartnerSign] = useState<string>('libra');
  const [dialogText, setDialogText] = useState<string>('');
  const [applyBothWays, setApplyBothWays] = useState<boolean>(true);

  const [savedNotification, setSavedNotification] = useState<string | null>(null);

  const configuredCount = Object.keys(zodiacPairs).length;

  const handleOpenEdit = (userSign: string, partnerSign: string) => {
    const key = `${userSign}_${partnerSign}`;
    setDialogUserSign(userSign);
    setDialogPartnerSign(partnerSign);
    setDialogText(zodiacPairs[key] || '');
    setApplyBothWays(true);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSaveFromDialog = () => {
    onSavePair(dialogUserSign, dialogPartnerSign, dialogText, applyBothWays);
    const uTitle = getZodiacTitle(dialogUserSign);
    const pTitle = getZodiacTitle(dialogPartnerSign);
    const msg = dialogText.trim()
      ? `Сохранено: ответ для пары «${uTitle} + ${pTitle}»`
      : `Сохранено: ответ для пары «${uTitle} + ${pTitle}» сброшен`;
    setSavedNotification(msg);
    setDialogOpen(false);
    setTimeout(() => {
      setSavedNotification(null);
    }, 5000);
  };

  const handleDeleteFromDialog = () => {
    const key = `${dialogUserSign}_${dialogPartnerSign}`;
    const reverseKey = `${dialogPartnerSign}_${dialogUserSign}`;
    const uTitle = getZodiacTitle(dialogUserSign);
    const pTitle = getZodiacTitle(dialogPartnerSign);
    onDeletePair(key, reverseKey);
    setSavedNotification(`Сохранено: ответ для пары «${uTitle} + ${pTitle}» удален`);
    setDialogOpen(false);
    setTimeout(() => {
      setSavedNotification(null);
    }, 5000);
  };

  // Фильтрация списка настроенных пар
  const filteredList = useMemo(() => {
    return Object.entries(zodiacPairs).filter(([key, text]) => {
      if (searchQuery.trim()) {
        const [u, p] = key.split('_');
        const q = searchQuery.toLowerCase();
        const uTitle = getZodiacTitle(u).toLowerCase();
        const pTitle = getZodiacTitle(p).toLowerCase();
        const matchSigns = uTitle.includes(q) || pTitle.includes(q);
        const matchText = text.toLowerCase().includes(q);
        return matchSigns || matchText;
      }
      return true;
    });
  }, [zodiacPairs, searchQuery]);

  const activePairKey = `${dialogUserSign}_${dialogPartnerSign}`;
  const hasActiveText = Boolean(zodiacPairs[activePairKey]);

  return (
    <Box sx={{mt: 2}}>
      {/* Панель управления и фильтры */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1.5
      }}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <Tabs
            value={viewMode}
            onChange={(e, val) => setViewMode(val)}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0.5,
                px: 2,
                fontSize: '0.85rem',
                fontWeight: 700,
                textTransform: 'none'
              }
            }}
          >
            <Tab value="matrix" label="Западные настройки"/>
            <Tab
              value="list"
              label={
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.8}}>
                  <span>Настроенные ответы</span>
                  <Chip
                    size="small"
                    label={configuredCount}
                    color={configuredCount > 0 ? 'primary' : 'default'}
                    sx={{height: 20, fontSize: '0.75rem', fontWeight: 700}}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>
      </Box>

      {savedNotification && (
        <Alert
          severity="success"
          onClose={() => setSavedNotification(null)}
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(46,125,50,0.15)',
            border: '1px solid #81c784',
            bgcolor: 'rgba(232, 245, 233, 0.9)',
            alignItems: 'center',
            fontWeight: 600
          }}
        >
          {savedNotification}
        </Alert>
      )}

      {/* ВИД 1: ЗАПАДНЫЕ НАСТРОЙКИ (ТАБЛИЦА) */}
      {viewMode === 'matrix' && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            mb: 2,
            bgcolor: 'transparent',
            background: 'transparent',
            border: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'transparent',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <AutoFixHighIcon sx={{color: '#1976d2', fontSize: 20}}/>
              <Typography variant="subtitle2" sx={{fontWeight: 700, color: '#1a237e'}}>
                Таблица совместимости: строки — знак пользователя, столбцы — знак партнера
              </Typography>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, fontSize: '0.75rem', color: '#546e7a'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Box sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '3px',
                  bgcolor: 'rgba(46, 125, 50, 0.15)',
                  border: '1px solid #81c784'
                }}/>
                <span>Настроен ответ</span>
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Box sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '3px',
                  bgcolor: 'transparent',
                  border: '1px solid #cfd8dc'
                }}/>
                <span>По умолчанию</span>
              </Box>
            </Box>
          </Box>

          <TableContainer sx={{maxHeight: 650, overflowX: 'auto', bgcolor: 'transparent', width: '100%'}}>
            <Table
              size="small"
              stickyHeader
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                bgcolor: 'transparent'
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: 'rgba(240, 244, 248, 0.92)',
                      backdropFilter: 'blur(8px)',
                      zIndex: 4,
                      position: 'sticky',
                      left: 0,
                      top: 0,
                      width: {xs: 105, sm: 120, md: 135},
                      borderRight: '2px solid rgba(0, 0, 0, 0.12)',
                      borderBottom: '2px solid rgba(0, 0, 0, 0.15)',
                      p: 0.6
                    }}
                  />
                  {ZODIAC_SIGNS.map(pSign => (
                    <TableCell
                      key={pSign.key}
                      align="center"
                      sx={{
                        bgcolor: 'rgba(240, 244, 248, 0.85)',
                        backdropFilter: 'blur(8px)',
                        zIndex: 3,
                        p: 0.4,
                        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                        borderBottom: '2px solid rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3}}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '7px',
                            background: pSign.bgGradient,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }}
                          title={`${pSign.title} • ${pSign.elementTitle}`}
                        >
                          <ZodiacIcon signKey={pSign.key} size={20} color="#ffffff"/>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: {xs: '0.62rem', sm: '0.7rem'},
                            color: '#263238',
                            lineHeight: 1.1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            display: 'block'
                          }}
                        >
                          {pSign.title}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ZODIAC_SIGNS.map(uSign => {
                  return (
                    <TableRow
                      key={uSign.key}
                      hover
                      sx={{bgcolor: 'transparent'}}
                    >
                      {/* Первая фиксированная колонка со знаком пользователя */}
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          bgcolor: 'rgba(244, 247, 250, 0.88)',
                          backdropFilter: 'blur(8px)',
                          fontWeight: 700,
                          borderRight: '2px solid rgba(0, 0, 0, 0.12)',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                          p: 0.6,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <ZodiacBadge signKey={uSign.key} size={28} fontSize="0.75rem"/>
                      </TableCell>

                      {/* Ячейки таблицы */}
                      {ZODIAC_SIGNS.map(pSign => {
                        const pairKey = `${uSign.key}_${pSign.key}`;
                        const customText = zodiacPairs[pairKey];
                        const isConfigured = Boolean(customText && customText.trim());

                        return (
                          <TableCell
                            key={pSign.key}
                            align="center"
                            onClick={() => handleOpenEdit(uSign.key, pSign.key)}
                            sx={{
                              p: 0.5,
                              cursor: 'pointer',
                              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                              bgcolor: isConfigured ? 'rgba(46, 125, 50, 0.12)' : 'transparent',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                bgcolor: isConfigured ? 'rgba(46, 125, 50, 0.22)' : 'rgba(25, 118, 210, 0.08)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                zIndex: 1
                              }
                            }}
                          >
                            <Tooltip
                              disableInteractive
                              enterDelay={0}
                              leaveDelay={0}
                              TransitionProps={{timeout: 0}}
                              PopperProps={{
                                sx: {pointerEvents: 'none'}
                              }}
                              title={
                                <Box sx={{p: 0.5}}>
                                  <Typography variant="caption" sx={{fontWeight: 700, display: 'block', mb: 0.5}}>
                                    Пользователь {uSign.title} + партнер {pSign.title}
                                  </Typography>
                                  {isConfigured ? (
                                    <Typography variant="body2" sx={{fontSize: '0.78rem'}}>
                                      «{customText}»
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" sx={{fontStyle: 'italic', opacity: 0.85}}>
                                      По умолчанию (нажмите, чтобы настроить)
                                    </Typography>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Box
                                sx={{
                                  minHeight: 34,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 1
                                }}
                              >
                                {isConfigured ? (
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: '#2e7d32',
                                      color: '#ffffff',
                                      borderRadius: '50%',
                                      width: 22,
                                      height: 22,
                                      boxShadow: '0 1px 3px rgba(46,125,50,0.4)'
                                    }}
                                  >
                                    <ChatBubbleOutlineIcon sx={{fontSize: 13}}/>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" sx={{color: '#b0bec5', fontWeight: 600}}>
                                    •
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ВИД 2: СПИСОК НАСТРОЕННЫХ ПАР */}
      {viewMode === 'list' && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2.5,
            mb: 2,
            bgcolor: 'transparent',
            background: 'transparent',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1.5
          }}>
            <TextField
              size="small"
              placeholder="Поиск по знакам или тексту..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{minWidth: 260}}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                  </InputAdornment>
                )
              }}
            />
            <Typography variant="caption" sx={{color: '#546e7a', fontWeight: 600}}>
              Показано: {filteredList.length} из {configuredCount} настроенных пар
            </Typography>
          </Box>

          {filteredList.length === 0 ? (
            <Alert severity="info" sx={{borderRadius: 2}}>
              {configuredCount === 0
                ? 'Индивидуальные ответы для знаков зодиака еще не созданы. Кликните по любой ячейке в таблице «Западные настройки».'
                : 'По заданному фильтру ничего не найдено.'}
            </Alert>
          ) : (
            <TableContainer sx={{maxHeight: 500, borderRadius: 2, bgcolor: 'transparent'}}>
              <Table size="small" stickyHeader sx={{bgcolor: 'transparent'}}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      fontWeight: 800,
                      width: 220,
                      bgcolor: 'rgba(240, 244, 248, 0.85)',
                      backdropFilter: 'blur(8px)',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>Знак пользователя</TableCell>
                    <TableCell sx={{
                      fontWeight: 800,
                      width: 60,
                      bgcolor: 'rgba(240, 244, 248, 0.85)',
                      backdropFilter: 'blur(8px)',
                      textAlign: 'center',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                    }}></TableCell>
                    <TableCell sx={{
                      fontWeight: 800,
                      width: 220,
                      bgcolor: 'rgba(240, 244, 248, 0.85)',
                      backdropFilter: 'blur(8px)',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>Знак партнера</TableCell>
                    <TableCell sx={{
                      fontWeight: 800,
                      bgcolor: 'rgba(240, 244, 248, 0.85)',
                      backdropFilter: 'blur(8px)',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>Текст ответа</TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800,
                      width: 110,
                      bgcolor: 'rgba(240, 244, 248, 0.85)',
                      backdropFilter: 'blur(8px)',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.map(([key, text]) => {
                    const [u, p] = key.split('_');
                    return (
                      <TableRow key={key} hover sx={{bgcolor: 'transparent'}}>
                        <TableCell sx={{borderBottom: '1px solid rgba(0, 0, 0, 0.06)'}}>
                          <ZodiacBadge signKey={u} size={28}/>
                        </TableCell>
                        <TableCell align="center" sx={{borderBottom: '1px solid rgba(0, 0, 0, 0.06)'}}>
                          <SwapHorizIcon sx={{color: '#90a4ae', fontSize: 20}}/>
                        </TableCell>
                        <TableCell sx={{borderBottom: '1px solid rgba(0, 0, 0, 0.06)'}}>
                          <ZodiacBadge signKey={p} size={28}/>
                        </TableCell>
                        <TableCell sx={{
                          maxWidth: 360,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                        }}>
                          <Tooltip disableInteractive enterDelay={0} leaveDelay={0} TransitionProps={{timeout: 0}}
                                   PopperProps={{sx: {pointerEvents: 'none'}}} title={text} arrow placement="top">
                            <Typography variant="body2" sx={{fontSize: '0.82rem', color: '#263238', lineHeight: 1.35}}>
                              {text}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{borderBottom: '1px solid rgba(0, 0, 0, 0.06)'}}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEdit(u, p)}
                            title="Редактировать ответ"
                          >
                            <EditIcon fontSize="small"/>
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeletePair(key, `${p}_${u}`)}
                            title="Удалить индивидуальный ответ"
                          >
                            <DeleteOutlineIcon fontSize="small"/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ДИАЛОГ РЕДАКТИРОВАНИЯ ПАРЫ */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {borderRadius: 3, p: 1}
        }}
      >
        <DialogTitle sx={{pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography variant="h6" sx={{fontWeight: 700, color: '#1a237e'}}>
            Настройка ответа для пары
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog}>
            <CloseIcon fontSize="small"/>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{py: 2}}>
          <Stack spacing={2.5}>
            {/* Визуальная карточка пары с иконками */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #f5f9ff 0%, #edf4fc 100%)',
                border: '1px solid #bbdefb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                flexWrap: 'wrap',
                gap: 1.5
              }}
            >
              <Box sx={{textAlign: 'center'}}>
                <Typography variant="caption" sx={{display: 'block', color: '#546e7a', fontWeight: 600, mb: 0.5}}>
                  Пользователь
                </Typography>
                <ZodiacBadge signKey={dialogUserSign} size={36} fontSize="0.95rem"/>
              </Box>

              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <SwapHorizIcon sx={{color: '#1976d2', fontSize: 32}}/>
                <Typography variant="caption" sx={{fontWeight: 700, color: '#1565c0'}}>
                  Пара
                </Typography>
              </Box>

              <Box sx={{textAlign: 'center'}}>
                <Typography variant="caption" sx={{display: 'block', color: '#546e7a', fontWeight: 600, mb: 0.5}}>
                  Партнер
                </Typography>
                <ZodiacBadge signKey={dialogPartnerSign} size={36} fontSize="0.95rem"/>
              </Box>
            </Paper>

            {/* Текстовое поле ответа */}
            <TextField
              label={`Ответ при проверке для «${getZodiacTitle(dialogUserSign)} + ${getZodiacTitle(dialogPartnerSign)}»`}
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={dialogText}
              onChange={(e) => setDialogText(e.target.value)}
              placeholder={`Например: Ваш партнер (${getZodiacTitle(dialogPartnerSign)}) с вами несовместим по дате рождения...`}
              helperText="Если оставить поле пустым или удалить текст, для этой пары будет использоваться общий вердикт несовместимости."
              autoFocus
            />

            {/* Чекбокс симметричности */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyBothWays}
                  onChange={(e) => setApplyBothWays(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{fontSize: '0.85rem', color: '#37474f'}}>
                  Также применить этот ответ для обратной пары
                  (<b>{getZodiacTitle(dialogPartnerSign)} + {getZodiacTitle(dialogUserSign)}</b>)
                </Typography>
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{px: 3, py: 1.8, justifyContent: 'space-between'}}>
          <Box>
            {hasActiveText && (
              <Button
                color="error"
                onClick={handleDeleteFromDialog}
                startIcon={<DeleteOutlineIcon/>}
                sx={{textTransform: 'none', fontWeight: 600}}
              >
                Удалить ответ
              </Button>
            )}
          </Box>
          <Box sx={{display: 'flex', gap: 1}}>
            <Button onClick={handleCloseDialog} sx={{textTransform: 'none'}}>
              Отмена
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveFromDialog}
              startIcon={<CheckCircleIcon/>}
              sx={{textTransform: 'none', fontWeight: 700, px: 2.5}}
            >
              Сохранить
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZodiacMatrixTable;
